import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, IconButton, Modal, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import moment from "moment";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { SubscriptionContext } from "../../../contexts/SubscriptionContext";
import theme from "../../../theme";
import { SubscriptionMonthlyLimit, SubscriptionPlan } from "../../organisms/subscriptions/SubscriptionSelector";

const useStyles = makeStyles(() => ({
    modal:  {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'fit-content',
        height: 'fit-content',
        minHeight: 250,
        background: '#fff',
        border: `1px solid ${theme.colors.neutral[600]}`,
        borderRadius: 24,
        boxShadow: theme.shadows[24],
    },
    closeButton: {
        color: theme.colors.neutral[500],
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 50,
        height: 50,
    },
    mainText: {
        fontFamily: 'Inter',
        fontSize: '1.4rem',
        fontWeight: 'bold',
        color: '#000',
        textAlign: 'center',
    },
    text: {
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        color: '#666666',
        textAlign: 'center',
    },
    updateButton: {
        padding: '4px 32px',
        borderRadius: 24,
        fontSize: '1.1rem',
        fontWeight: 'bold',
        textTransform: 'none',
    },
}));

const PlanModal: React.FC<{
    variant: string | 'limit' | 'upgrade',
    exceptPlan?: string[],
 }> = ({ variant, exceptPlan }) => {
    const classes = useStyles();
    const history = useHistory();
    const { subscription, usage } = useContext(SubscriptionContext);
    const [currentLimit, setCurrentLimit] = useState<SubscriptionMonthlyLimit|null>(null);
    const [renewal, setRenewal] = useState<number>(0);
    const [nextPlan, setNextPlan] = useState<string|null>(null);
    const [planModalOpen, setPlanModelOpen] = useState<boolean>(false);

    const timeOutRef = useRef<NodeJS.Timeout | string | number | undefined>(undefined);

    const handleUpgrade = useCallback(() => {
        history.push('/subscriptions');
        setPlanModelOpen(false);
    }, [history]);

    useEffect(() => {
        if (!!currentLimit && !!subscription && !!usage && !timeOutRef.current) {
            timeOutRef.current = setTimeout(() => {
                clearTimeout(timeOutRef.current);
                switch (variant) {
                    case 'limit':
                        setPlanModelOpen((usage?.dashboardsCreated ?? 0) >= currentLimit);
                        break;
                    case 'upgrade':
                        if (exceptPlan?.map(plan => plan.toLowerCase()).includes(subscription.planName!.toLowerCase()))
                            setPlanModelOpen(true);
                        break;
                }
            }, 1000);
        }
    // eslint-disable-next-line
    }, [currentLimit, subscription, usage]);

    useEffect(() => {
        if (subscription) {
            switch (subscription.planName?.toLowerCase()) {
                case SubscriptionPlan.PowerUser:
                    setCurrentLimit(SubscriptionMonthlyLimit.PowerUser);
                    break;
                case SubscriptionPlan.Growth_v1:
                    setCurrentLimit(SubscriptionMonthlyLimit.Growth_v1);
                    setNextPlan('Power plan');
                    break;
                case SubscriptionPlan.Pro_v2:
                case SubscriptionPlan.Pro_v1:
                case SubscriptionPlan.Pro_Legacy:
                    setCurrentLimit(SubscriptionMonthlyLimit.Pro_v2);
                    setNextPlan('Growth plan');
                    break;
                case SubscriptionPlan.Free:
                default:
                    setCurrentLimit(SubscriptionMonthlyLimit.Free);
                    setNextPlan('Pro plan');
                    break;
            }
            setRenewal(subscription?.currentPeriodEndedAt
                ? Math.max(0, Math.ceil(moment(subscription.currentPeriodEndedAt).diff(moment(), 'days', true)))
                : 0);
        }
    }, [subscription]);

    return (<>
        <Modal open={planModalOpen} disableEscapeKeyDown
            onClose={(_, reason) => reason !== 'backdropClick' ? setPlanModelOpen(false) : null}
            sx={{ zIndex: 1800, backdropFilter: 'blur(1px)' }}>
            <Stack className={classes.modal} p={1}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%">
                    {variant === 'limit' && (
                        <IconButton className={classes.closeButton} onClick={() => setPlanModelOpen(false)}>
                            <CloseIcon fontSize="large" />
                        </IconButton>
                    )}
                </Stack>
                {variant === "limit" ? (<>
                    <Stack spacing={3} alignItems="center" justifyContent="center" height="100%" width="450px" pb="32px">
                        <ErrorIcon className={classes.icon} sx={{ transform: 'translateX(-5px)' }} />
                        <Typography className={classes.mainText}>
                            {`You are out of companies`}<br />
                            {`for this month`}
                        </Typography>
                        <Typography className={classes.text}>
                            {`Your usage resets in `}<b>{`${renewal} day${renewal > 1 ? 's' : ''}`}</b>.
                        </Typography>
                        <Typography className={classes.text} pb={3}>
                            {`To increase your monthy usage limit, `}<br />
                            {`upgrade your plan.`}
                        </Typography>
                        <Button variant="contained"
                            className={classes.updateButton}
                            onClick={handleUpgrade}>
                            {'Upgrade'}
                        </Button>
                    </Stack>
                </>) : (
                    <Stack spacing={2} alignItems="center" justifyContent="center" height="100%" p="16px 32px">
                        <Typography className={classes.text} pb={5}>
                            {'To see data here, upgrade to our'} <strong>{nextPlan}</strong>.
                        </Typography>
                        <Button variant="contained"
                            className={classes.updateButton}
                            onClick={handleUpgrade}>
                            {'Upgrade'}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Modal>
    </>);
}

export default PlanModal;